import { useCallback } from 'react'
import { trackEvent } from '@fs/zion-analytics'
import {
  useSlsStateContext,
  useSlsDispatchContext,
  getNestedOrFloatingElementById,
  getNextArkId,
  batchCreateRecord,
  generateSls,
  saveSlsAndUpdateState,
} from '@fs/zion-sls'
import { useFeatureFlags } from '@fs/zion-flags'
import { errorCollector } from '../../../../../shared/utils/utils'
import { FullTextApp, headers } from '../../../../../shared/services/config'

function openSourceLinker(personId) {
  const arkPath = `/ark:/61903/${personId}`
  const sourceLinkerUrl = `/search/linker?ark=${encodeURIComponent(arkPath)}&sourceType=rii`
  trackEvent({
    link_name: 'open-source-linker-from-attach-to-tree',
  })
  window.open(sourceLinkerUrl, '_blank') || window.location.assign(sourceLinkerUrl)
}

async function waitForRecordCreation(
  person,
  currentImageArk,
  zionSlsDispatch,
  slsActionsSplunkDebugger,
  saveChangesFlag
) {
  // Get a new ark for the record
  const recordArk = await getNextArkId('record')

  // Create the record
  const dispatches = batchCreateRecord({
    imagePartitionKey: currentImageArk,
    recordArk,
  })

  // Move the person to the new record
  dispatches.push({
    type: 'ADD_SUB',
    value: {
      superId: recordArk,
      subId: person.id,
      superPartitionKey: currentImageArk,
      subPartitionKey: person.partitionKey,
      elementType: 'RECORD',
    },
  })

  const sls = generateSls(dispatches)

  // Save the changes
  await saveSlsAndUpdateState({
    sls,
    dispatches,
    saveChangesFlag,
    slsActionsSplunkDebugger,
    dispatch: zionSlsDispatch,
    appName: FullTextApp,
    fsUserAgentChain: headers['FS-User-Agent-Chain'],
    successHandler: () => {
      openSourceLinker(person.id)
    },
    errorHandler: (err) => {
      errorCollector(err, currentImageArk, {
        codeId: 60,
      })
    },
    saveInitiatorFilename: 'useAttachToTreePersonSelect.js',
  })
  // Give sls queue some time to create the record.
  return new Promise((res) => {
    setTimeout(res, 1000)
  })
}

/**
 * Hook that provides the interaction handler for selecting a person in the Attach to Tree context.
 * This encapsulates the logic for opening Source Linker.
 * If the person is floating (no record parent), it creates a record and adds the person to it first.
 *
 * @returns {Function} The handlePersonSelect function
 */
export default function useAttachToTreePersonSelect() {
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])

  return useCallback(
    async (personId) => {
      // Find the person in the SLS state
      const person = getNestedOrFloatingElementById(zionSlsState.images, personId)

      if (!person) {
        console.error('useAttachToTreePersonSelect: Person not found', personId)
        return
      }

      const isFloating = !person.superElements?.length
      if (isFloating) {
        try {
          await waitForRecordCreation(
            person,
            zionSlsState.currentImageArk,
            zionSlsDispatch,
            slsActionsSplunkDebugger,
            saveChangesFlag
          )
        } catch (error) {
          errorCollector(error, zionSlsState.currentImageArk, {
            codeId: 60,
          })
        }
      } else {
        // Person already has a record, just open Source Linker
        openSourceLinker(personId)
      }
    },
    [zionSlsState.images, zionSlsState.currentImageArk, zionSlsDispatch, saveChangesFlag, slsActionsSplunkDebugger]
  )
}
